import { useMemo, useState } from 'react';

import { useStateContext } from '../../contexts/ContextProvider';
import { shouldShowSendingFacility } from '../../helpers/sendingFacility';
import { useSendingFacility } from '../../hooks/useSendingFacility';
import type { Customer } from '../../pages/cardex/api/useCustomer';
import type { Message } from '../../pages/cardex/api/useMessage';
import type { Reservation } from '../../pages/cardex/api/useReservation';

import BandeauCanaux, { canauxJoignables } from './BandeauCanaux';
import BarreSaisie from './BarreSaisie';
import FeuilleMessagesRapides from './FeuilleMessagesRapides';
import FeuilleModeles from './FeuilleModeles';
import FeuilleModelesEmail from './FeuilleModelesEmail';
import LigneEnvoyeEnTantQue from './LigneEnvoyeEnTantQue';
import MurWhatsapp from './MurWhatsapp';
import OutilsRedaction from './OutilsRedaction';
import Text from './Text';
import { fenetreOuverte } from '../hooks/useFenetreWhatsapp';
import { useBrouillonPersistant } from '../hooks/useBrouillonPersistant';
import { useEnvoiComposeur } from '../hooks/useEnvoiComposeur';
import { useSourcesComposeur } from '../hooks/useSourcesComposeur';
import { ACTION_TYPE_EMAIL } from '../config/canaux';
import type { CleCanal } from '../config/canaux';
import { t } from '../i18n/langue';

/**
 * Le composeur — #84.
 *
 * Il assemble trois pièces qui répondent chacune à une décision : le **bandeau**
 * (#16), la **barre** (#16) et le **mur** (#19). La règle qui les relie tient en
 * une phrase de #19 : **le composeur ne montre que ce qui aboutit**.
 *
 * **Il écrit au nom de l'hôtel de la conversation**, jamais de l'établissement
 * sélectionné (retour Odomo 2026-09 : en vue groupe, c'était le groupe).
 *
 * **Rien n'est chargé tant qu'on n'ouvre pas.** Modèles et messages rapides ne
 * partent qu'à l'ouverture de leur feuille : #31 a ramené le fil de onze requêtes à
 * quatre, ce n'est pas pour que le composeur en rajoute au chargement.
 */
interface ComposeurProps {
    client: Customer | undefined;
    messages: Message[];
    reservations: Reservation[];
    onEnvoye?: () => void;
    /**
     * Le fil a-t-il fini de charger ses messages ? — audit 2026-09-23.
     *
     * Tant que ce n'est pas le cas, `canauxJoignables` n'a vu ni e-mail, ni
     * téléphone, ni le live chat déduit des messages : « impossible de lui
     * répondre » serait annoncé sur une donnée qui n'est pas encore arrivée, pas
     * sur une absence réelle.
     */
    messagesPrets: boolean;
}

export default function Composeur({ client, messages, reservations, onEnvoye, messagesPrets }: ComposeurProps) {
    const { currentFacility, user } = useStateContext();
    const { options: identites, sendingFacility: identite, chooseSendingFacility } = useSendingFacility(client);

    const canaux = useMemo(() => canauxJoignables(client, messages), [client, messages]);
    const [canalChoisi, setCanalChoisi] = useState<CleCanal | null>(null);
    const canal = canalChoisi ?? canaux[0]?.cle ?? null;

    // Persistant (#86) : il survit à un rechargement, donc à une PWA que iOS a
    // tuée en arrière-plan. Ce n'est PAS une file d'envoi — voir le hook.
    const [brouillon, setBrouillon, viderBrouillon] = useBrouillonPersistant(user?.id, client?.id);
    const [objet, setObjet] = useState<string | null>(null);
    const [outilsOuverts, setOutilsOuverts] = useState(false);
    const [rapidesOuverts, setRapidesOuverts] = useState(false);
    const [modelesOuverts, setModelesOuverts] = useState(false);
    const [modelesEmailOuverts, setModelesEmailOuverts] = useState(false);

    const { rapides, modeles, modelesEmail, modelesEnEchec, modelesEnChargement, modelesEmailEnEchec, modelesEmailEnChargement } =
        useSourcesComposeur({
            facilityId: identite?.facility_id ?? null,
            canal,
            rapidesOuverts,
            modelesOuverts,
            modelesEmailOuverts,
        });
    const surWhatsapp = canal === 'whatsapp';
    const surEmail = canal === 'email';

    /**
     * L'objet par défaut : « Re: » du dernier e-mail du fil.
     *
     * Le serveur REFUSE un e-mail sans sujet (`MessageController.php:619`) — relevé
     * en envoyant, pas en lisant. Pré-remplir évite de demander à quelqu'un qui
     * répond au pouce d'inventer un objet ; le champ reste modifiable.
     */
    const objetParDefaut = useMemo(() => {
        const dernierEmail = [...messages]
            .reverse()
            .find((m) => m.action_type_id === ACTION_TYPE_EMAIL && m.object);
        if (!dernierEmail?.object) return '';
        return /^re\s*:/i.test(dernierEmail.object) ? dernierEmail.object : `Re: ${dernierEmail.object}`;
    }, [messages]);
    // La fenêtre de 24 h de la fiche de l'hôtel d'envoi : c'est elle que le serveur vérifiera.
    const fenetreJusqua = identite ? identite.whatsapp_free_text_until : client?.whatsapp_free_text_until;
    const murDresse = surWhatsapp && !fenetreOuverte(fenetreJusqua);

    /** Ce qui aboutit réellement pour CETTE cliente (#19 §7). */
    const modelesProposables = useMemo(() => {
        const tous = modeles.data ?? [];
        return tous.filter((m) => !m.requires_reservation || reservations.length > 0);
    }, [modeles.data, reservations.length]);

    const { erreur, envoyerTexte, envoyerModele, envoyerModeleEmail, envoiEnCours } = useEnvoiComposeur({
        client,
        identite,
        canal,
        brouillon,
        viderBrouillon,
        surEmail,
        objet,
        setObjet,
        objetParDefaut,
        onEnvoye,
    });

    if (client && canal === null && messagesPrets) {
        return (
            <Text as="p" variante="petit" ton="attenue" className="border-t border-border-color-light bg-white px-4 py-3">
                {t('composeur.aucunCanal')}
            </Text>
        );
    }
    if (!client || canal === null) return null;

    return (
        <>
            <BandeauCanaux canaux={canaux} actif={canal} onChange={setCanalChoisi} />

            {shouldShowSendingFacility(identites, currentFacility?.id, !!currentFacility?.is_group) && (
                <LigneEnvoyeEnTantQue options={identites} identite={identite} onChoisir={chooseSendingFacility} />
            )}

            {murDresse ? (
                <MurWhatsapp
                    fenetreJusqua={fenetreJusqua}
                    nbModeles={modelesProposables.length}
                    chargement={modelesEnChargement}
                    erreur={modelesEnEchec && !modelesEnChargement}
                    onReessayer={() => modeles.refetch()}
                    autresCanaux={canaux.filter((c) => c.cle !== 'whatsapp')}
                    onOuvrirModeles={() => setModelesOuverts(true)}
                    onChangerCanal={setCanalChoisi}
                />
            ) : (
                <BarreSaisie
                    valeur={brouillon}
                    onChange={setBrouillon}
                    objet={
                        surEmail
                            ? { valeur: objet ?? objetParDefaut, onChange: setObjet }
                            : undefined
                    }
                    onEnvoyer={envoyerTexte}
                    onOuvrirOutils={() => setOutilsOuverts(true)}
                    enVol={envoiEnCours}
                    erreur={erreur}
                />
            )}

            <FeuilleModeles
                ouverte={modelesOuverts}
                onClose={() => setModelesOuverts(false)}
                modeles={modeles.data ?? []}
                reservations={reservations}
                locale={client.language_locale}
                onChoisir={(modele, reservationId) => {
                    setModelesOuverts(false);
                    envoyerModele(modele, reservationId);
                }}
                chargement={modelesEnChargement}
                erreur={modelesEnEchec && !modelesEnChargement}
                onReessayer={() => modeles.refetch()}
            />

            <FeuilleModelesEmail
                ouverte={modelesEmailOuverts}
                onClose={() => setModelesEmailOuverts(false)}
                modeles={modelesEmail.data ?? []}
                locale={client.language_locale}
                sujetDeRepli={objet ?? objetParDefaut}
                onChoisir={(modele, sujet) => {
                    setModelesEmailOuverts(false);
                    envoyerModeleEmail(modele, sujet);
                }}
                chargement={modelesEmailEnChargement}
                erreur={modelesEmailEnEchec && !modelesEmailEnChargement}
                onReessayer={() => modelesEmail.refetch()}
            />

            {/* Les gardés de #84, sous le « + » — dont le modèle d'e-mail, retour client 2026-09-25. */}
            <OutilsRedaction
                ouverte={outilsOuverts}
                onClose={() => setOutilsOuverts(false)}
                surWhatsapp={surWhatsapp}
                surEmail={surEmail}
                onOuvrirMessagesRapides={() => {
                    setOutilsOuverts(false);
                    setRapidesOuverts(true);
                }}
                onOuvrirModeles={() => {
                    setOutilsOuverts(false);
                    setModelesOuverts(true);
                }}
                onOuvrirModelesEmail={() => {
                    setOutilsOuverts(false);
                    setModelesEmailOuverts(true);
                }}
            />

            <FeuilleMessagesRapides
                ouverte={rapidesOuverts}
                onClose={() => setRapidesOuverts(false)}
                rapides={rapides}
                onChoisir={(contenu) => {
                    setBrouillon(contenu);
                    setRapidesOuverts(false);
                }}
            />
        </>
    );
}
